import { useMemo } from "react"
import { first, isNumber } from "lodash"
import { VALUE_FORMATS } from "helpers/ai"
import { getForcedCurrencyScale } from "helpers/formatters/numbers"
import { buildScaledCurrencyLabel } from "helpers/metrics/currency-scale-label"
import { formatValueForDisplay, getColumnType, parseValue } from "connected-components/ai-teammate/helpers/formatting"
import { isPivotable } from "connected-components/ai-teammate/helpers"
import { isPerSharePriceColumn } from "connected-components/ai-teammate/helpers/per-share-price-column"
import type { ArtifactColumn } from "connected-components/dashboard/modules/ai-artifact/types"
import type { GridColDef } from "@mui/x-data-grid-premium"

export type TableArtifactColumn = GridColDef & { displayHeader: string }

export interface UseTableArtifactDataReturn {
  columns: TableArtifactColumn[]
  rows: Record<string, unknown>[]
  columnHeaders: string[]
  columnFormats: Record<string, string>
}

export interface TableArtifactDataInput {
  columns: ArtifactColumn[]
  records: Record<string, unknown>[]
}

const MIN_SCALED_CURRENCY_MAGNITUDE = 10000
const MISSING_SCALED_VALUE_LABEL = "-"

function toCanonicalKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "")
}

/**
 * Resolve a value from a data row using accessor candidates.
 * Tries exact key match first, then canonicalized match (ignores case/spaces/symbols).
 */
function getValueFromRecord(record: Record<string, unknown>, accessorCandidates: string[]) {
  const directMatchAccessor = accessorCandidates
    .filter(Boolean)
    .find(candidate => Object.hasOwn(record, candidate))

  if (directMatchAccessor) {
    return record[directMatchAccessor]
  }

  const canonicalCandidates = new Set(accessorCandidates
    .filter(Boolean)
    .map(toCanonicalKey))

  if (canonicalCandidates.size === 0) {
    return undefined
  }

  return Object.entries(record)
    .find(([recordKey]) => canonicalCandidates.has(toCanonicalKey(recordKey)))
    ?.[1]
}

function getCurrencyScaleForColumn(rows: Record<string, unknown>[], fieldName: string): string | null {
  const maxMagnitude = rows.reduce((largestMagnitude, row) => {
    const value = parseValue(row[fieldName], VALUE_FORMATS.CURRENCY)
    return Number.isFinite(value) ? Math.max(largestMagnitude, Math.abs(value)) : largestMagnitude
  }, 0)
  return maxMagnitude >= MIN_SCALED_CURRENCY_MAGNITUDE ? getForcedCurrencyScale(maxMagnitude) : null
}

/**
 * Build DataGrid-ready columns/rows from an AI table artifact payload.
 * Columns are normalized to col_0, col_1, etc.; rows are mapped using metadata accessors.
 * A currency column whose largest absolute value is at least 10,000 gets one $K or $M scale:
 * `displayHeader` and `renderHeader` carry the unit, cells are value-only and `headerName` stays bare.
 * Per-share price columns are never scaled: a boolean `isPerShare` decides, and when it is absent a label with price, px or per share does.
 * Those and all other columns keep per-value formatting and `displayHeader` equals `headerName`.
 */
export function useTableArtifactData(artifact: TableArtifactDataInput, currency?: string): UseTableArtifactDataReturn {
  const { columns: rawColumns, records } = artifact

  const hasRecords = records?.length > 0
  const hasColumnMetadata = rawColumns?.length > 0
  const firstRecord = first(records)

  const columnAccessors = useMemo(() => {
    if (!hasColumnMetadata) {
      return []
    }
    return rawColumns.map((column) => {
      return column.id || column.field || column.key || column.name || column.label
    })
  }, [hasColumnMetadata, rawColumns])

  const columnHeaders = useMemo(() => {
    if (hasColumnMetadata) {
      return rawColumns.map((col) => col.label)
    }
    return firstRecord ? Object.keys(firstRecord) : []
  }, [rawColumns, firstRecord, hasColumnMetadata])

  const columnFormats = useMemo(() => {
    return columnHeaders.reduce<Record<string, string>>((formats, key, index) => {
      const fieldName = `col_${index}`
      if (hasColumnMetadata) {
        const columnMetadata = rawColumns[index]
        formats[fieldName] = columnMetadata.format
      } else {
        formats[fieldName] = isNumber(firstRecord?.[key]) ? VALUE_FORMATS.NUMBER : VALUE_FORMATS.STRING
      }
      return formats
    }, {})
  }, [columnHeaders, hasColumnMetadata, rawColumns, firstRecord])

  const rows = useMemo(() => {
    if (!hasRecords) return []

    return records.map((record, rowIndex) => {
      return columnHeaders.reduce<Record<string, unknown>>(
        (mappedRecord, columnHeader, colIndex) => {
          const fieldName = `col_${colIndex}`
          const accessorCandidates = hasColumnMetadata
            ? [columnAccessors[colIndex], columnHeader]
            : [columnHeader]
          mappedRecord[fieldName] = getValueFromRecord(record, accessorCandidates)
          return mappedRecord
        },
        { id: rowIndex },
      )
    })
  }, [hasRecords, records, columnHeaders, hasColumnMetadata, columnAccessors])

  const columns = useMemo(() => {
    return columnHeaders.map((headerName, index) => {
      const fieldName = `col_${index}`
      const format = columnFormats[fieldName]
      const isScaleCandidate = format === VALUE_FORMATS.CURRENCY && !isPerSharePriceColumn(rawColumns?.[index])
      const currencyScale = isScaleCandidate ? getCurrencyScaleForColumn(rows, fieldName) : null
      const displayHeader = buildScaledCurrencyLabel(headerName, currencyScale, currency)

      const baseColumn = {
        field: fieldName,
        headerName,
        displayHeader,
        type: getColumnType(format),
        minWidth: 50,
        valueGetter: (value: unknown) => parseValue(value, format),
        hideable: true,
        pivotable: isPivotable(headerName, records),
        disableReorder: false,
      } satisfies Partial<TableArtifactColumn>

      if (!currencyScale) {
        return {
          ...baseColumn,
          valueFormatter: (value: unknown) => formatValueForDisplay(value, format, currency),
        } satisfies TableArtifactColumn
      }

      return {
        ...baseColumn,
        renderHeader: () => displayHeader,
        valueFormatter: (value: unknown) => formatValueForDisplay(
          value,
          format,
          currency,
          { forcedScale: currencyScale, valueOnly: true },
        ) ?? MISSING_SCALED_VALUE_LABEL,
      } satisfies TableArtifactColumn
    })
  }, [columnHeaders, records, rawColumns, columnFormats, currency, rows])

  return {
    columns,
    rows,
    columnHeaders,
    columnFormats,
  }
}
